import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import {
  Dashboard,
  People,
  Storage,
  Receipt,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Logout,
  Settings,
  Hub,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

const drawerWidthExpanded = 280;
const drawerWidthCollapsed = 80;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export default function Sidebar({ open, onToggle, onClose }: SidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { logout,can } = useAuth();

  const navigationItems = [
    { label: 'Dashboard', icon: <Dashboard />, path: '/' },
    { label: 'Integradores', icon: <People />, path: '/integradores' },
    { label: 'Instâncias', icon: <Storage />, path: '/instancias' },
    { label: 'Faturamento', icon: <Receipt />, path: '/faturamento' },
    { label: 'Planos e módulos', icon: <Settings />, path: '/comercial' },
    { label: 'Usuários do Management', icon: <People />, path: '/management-users' },
    { label: 'Infraestrutura', icon: <Hub />, path: '/infraestrutura' },
  ];

  const handleLogout = () => {
    logout();
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: '64px',
        }}
      >
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }}
        >
          Solar Management
        </Typography>
        {!isMobile && (
          <IconButton onClick={onToggle} aria-label={open ? 'Recolher menu' : 'Expandir menu'}>
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        )}
      </Box>
      <Divider />

      {/* Navegação Principal */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {navigationItems.filter(item => item.path==='/comercial'?can('commercial.manage'):item.path==='/faturamento'?can('financial.manage'):item.path==='/infraestrutura'?can('infra.manage'):item.path==='/management-users'?can('users.manage'):true).map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={isMobile ? onClose : undefined}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                justifyContent: open ? 'initial' : 'center',
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  '& .MuiListItemIcon-root': { color: '#1976d2' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 2 }} />

      {/* Configurações e Logout */}
      <List sx={{ px: 1, py: 1 }}>
        <ListItemButton
          component={Link}
          to="/settings"
          onClick={isMobile ? onClose : undefined}
          selected={location.pathname.startsWith('/settings')}
          sx={{
            borderRadius: 2,
            mx: 1,
            justifyContent: open ? 'initial' : 'center',
            '&.Mui-selected': {
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              '& .MuiListItemIcon-root': { color: '#1976d2' },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
            }}
          >
            <Settings />
          </ListItemIcon>
          <ListItemText primary="Minha conta" sx={{ opacity: open ? 1 : 0 }} />
        </ListItemButton>

        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              mx: 1,
              justifyContent: open ? 'initial' : 'center',
              color: 'error.main',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Sair" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer (Temporary) */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidthExpanded,
              maxWidth: 'calc(100% - 32px)',
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop Drawer (Permanent) */
        <Drawer
          variant="permanent"
          sx={{
            width: open ? drawerWidthExpanded : drawerWidthCollapsed,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: open ? drawerWidthExpanded : drawerWidthCollapsed,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}
